// Onboarding Baseline Cohort SQL. The cohort is locked in the CTE chain:
// trailing 6 months excluding the last 30 days, active role, no support
// roles, no @gomarloo accounts, no instant-paid (<=1h post signup), and no
// one at the company has had a demo (excluding consumer email domains from
// that check). One row per (user, company) pair, same shape as SignupFunnel
// so the chart helpers can be re-used.

export type CohortMode =
  | "baseline"
  | "all_users"
  | "demo"
  | "support_no_demo"
  | "support_demo";

// Cohort selection CTEs, shared between baseline + activity queries so the
// definitions can't drift. Caller must prepend WITH and append its own CTEs.
// In "baseline" mode the cohort is the locked baseline (no support, no
// @gomarloo, no instant-paid, no demo-touched company). In "all_users" mode
// the cohort is every active user who signed up in the lookback window — no
// exclusions beyond the 30-day end cutoff. Same row shape so downstream CTEs
// don't need to branch.
export function cohortCtesSqlFor(mode: CohortMode): string {
  const endExpr = mode === "all_users"
    ? "now()::timestamptz"
    : "(now() - interval '30 days')::timestamptz";
  const baseCtes = `
window_bounds AS (
    SELECT (now() - interval '360 days')::timestamptz AS start_ts,
           ${endExpr}                                  AS end_ts
),

consumer_domains AS (
    SELECT unnest(ARRAY[
        'gmail.com','googlemail.com','yahoo.com','ymail.com','rocketmail.com',
        'hotmail.com','hotmail.co.uk','hotmail.co.nz','hotmail.com.au',
        'outlook.com','outlook.co.nz','outlook.com.au','live.com','live.co.uk','live.com.au',
        'msn.com','aol.com','icloud.com','me.com','mac.com',
        'protonmail.com','proton.me','tutanota.com',
        'xtra.co.nz','vodafone.co.nz','slingshot.co.nz','clear.net.nz','inspire.net.nz',
        'bigpond.com','bigpond.net.au','optusnet.com.au','iinet.net.au','tpg.com.au',
        'btinternet.com','sky.com','virginmedia.com','tiscali.co.uk'
    ]) AS d
),

demo_attendees AS (
    SELECT DISTINCT lower(att.email) AS email
    FROM reporting.demo d
    CROSS JOIN LATERAL unnest(d.attendee_emails) AS att(email)
    WHERE (d.follow_up_context ->> 'is_demo') = 'true'
      AND d.organiser_email ILIKE '%@gomarloo.com'
),

-- Companies with at least one active user (non-consumer-domain) who has had a demo.
companies_with_demo AS (
    SELECT DISTINCT ur.company_external_id
    FROM public.user_roles ur
    JOIN public.users u ON u.external_id = ur.user_external_id
    JOIN demo_attendees da ON da.email = lower(u.email)
    WHERE ur.status = 'active'
      AND split_part(lower(u.email), '@', 2) NOT IN (SELECT d FROM consumer_domains)
),

billing_first_paid AS (
    SELECT cb.company_external_id,
           MIN(cb.subscription_started_date_utc) AS first_paid_ts
    FROM public.company_billing cb
    WHERE cb.billing_plan::text IN ('plus','pro')
      AND cb.subscription_started_date_utc IS NOT NULL
    GROUP BY cb.company_external_id
),

`;
  const baselineCohort = `
cohort AS (
    SELECT ur.user_external_id,
           ur.company_external_id
    FROM public.user_roles ur
    JOIN public.users u ON u.external_id = ur.user_external_id
    CROSS JOIN window_bounds w
    LEFT JOIN billing_first_paid bfp ON bfp.company_external_id = ur.company_external_id
    WHERE ur.status = 'active'
      AND ur.role::text <> 'support'
      AND u.email NOT ILIKE '%@gomarloo.com'
      AND u.created_at_utc >= w.start_ts
      AND u.created_at_utc <  w.end_ts
      AND NOT EXISTS (
          SELECT 1 FROM companies_with_demo cwd
          WHERE cwd.company_external_id = ur.company_external_id
      )
      AND (
          bfp.first_paid_ts IS NULL
          OR bfp.first_paid_ts > u.created_at_utc + interval '1 hour'
      )
)`;
  const allUsersCohort = `
cohort AS (
    SELECT ur.user_external_id,
           ur.company_external_id
    FROM public.user_roles ur
    JOIN public.users u ON u.external_id = ur.user_external_id
    CROSS JOIN window_bounds w
    WHERE ur.status = 'active'
      AND u.created_at_utc >= w.start_ts
      AND u.created_at_utc <  w.end_ts
)`;
  const demoCohort = `
cohort AS (
    SELECT ur.user_external_id,
           ur.company_external_id
    FROM public.user_roles ur
    JOIN public.users u ON u.external_id = ur.user_external_id
    CROSS JOIN window_bounds w
    LEFT JOIN billing_first_paid bfp ON bfp.company_external_id = ur.company_external_id
    WHERE ur.status = 'active'
      AND ur.role::text <> 'support'
      AND u.email NOT ILIKE '%@gomarloo.com'
      AND u.created_at_utc >= w.start_ts
      AND u.created_at_utc <  w.end_ts
      AND EXISTS (
          SELECT 1 FROM companies_with_demo cwd
          WHERE cwd.company_external_id = ur.company_external_id
      )
      AND (
          bfp.first_paid_ts IS NULL
          OR bfp.first_paid_ts > u.created_at_utc + interval '1 hour'
      )
)`;
  // Support-only cohorts: include ONLY users whose active role is `support`,
  // keep the @gomarloo + 360d window guards, drop the instant-paid exclusion.
  // The `_no_demo` flavour requires the company to have NO demo'd member;
  // `_demo` flips the EXISTS check. Together the two are mutually exclusive
  // on the demo dimension.
  const supportNoDemoCohort = `
cohort AS (
    SELECT ur.user_external_id,
           ur.company_external_id
    FROM public.user_roles ur
    JOIN public.users u ON u.external_id = ur.user_external_id
    CROSS JOIN window_bounds w
    WHERE ur.status = 'active'
      AND ur.role::text = 'support'
      AND u.email NOT ILIKE '%@gomarloo.com'
      AND u.created_at_utc >= w.start_ts
      AND u.created_at_utc <  w.end_ts
      AND NOT EXISTS (
          SELECT 1 FROM companies_with_demo cwd
          WHERE cwd.company_external_id = ur.company_external_id
      )
)`;
  const supportDemoCohort = `
cohort AS (
    SELECT ur.user_external_id,
           ur.company_external_id
    FROM public.user_roles ur
    JOIN public.users u ON u.external_id = ur.user_external_id
    CROSS JOIN window_bounds w
    WHERE ur.status = 'active'
      AND ur.role::text = 'support'
      AND u.email NOT ILIKE '%@gomarloo.com'
      AND u.created_at_utc >= w.start_ts
      AND u.created_at_utc <  w.end_ts
      AND EXISTS (
          SELECT 1 FROM companies_with_demo cwd
          WHERE cwd.company_external_id = ur.company_external_id
      )
)`;
  const cohort =
    mode === "baseline"        ? baselineCohort :
    mode === "demo"            ? demoCohort :
    mode === "support_no_demo" ? supportNoDemoCohort :
    mode === "support_demo"    ? supportDemoCohort :
                                 allUsersCohort;
  return baseCtes + cohort;
}

export const COHORT_CTES_SQL = cohortCtesSqlFor("baseline");
export const ALL_USERS_COHORT_CTES_SQL = cohortCtesSqlFor("all_users");

function buildBaselineSql(mode: CohortMode): string {
  return `
WITH ${cohortCtesSqlFor(mode)},

transcripts AS (
    SELECT
        t.user_external_id,
        t.company_external_id,
        COUNT(*)                AS transcript_count,
        MIN(t.created_date_utc) AS first_transcript_date,
        (array_agg(t.created_date_utc ORDER BY t.created_date_utc ASC))[2]
                                AS second_transcript_date,
        (array_agg(t.created_date_utc ORDER BY t.created_date_utc ASC))[5]
                                AS fifth_transcript_date
    FROM public.meeting_transcription t
    JOIN public.meeting m ON m.external_id = t.meeting_external_id
    JOIN cohort co ON co.user_external_id = t.user_external_id
                 AND co.company_external_id = t.company_external_id
    JOIN public.users u ON u.external_id = t.user_external_id
    WHERE m.is_demo = false
      AND m.deleted_date_utc IS NULL
      AND t.deleted_date_utc IS NULL
      AND t.created_date_utc >= u.created_at_utc
    GROUP BY t.user_external_id, t.company_external_id
),

-- Transcript -> synopsis rate in the first 14 days, deduped to one row per
-- (user, meeting). transcripts_14d_meeting_count is distinct meetings with a
-- non-demo transcript in the user's first 14 days; synopsised_14d_meeting_count
-- is the subset of those meetings that also has at least one synopsis (any
-- time) by the same user. Synopsis match is intentionally not capped at 14d
-- so a late-arriving summary on an early transcript still counts.
transcripts_14d AS (
    SELECT
        t.user_external_id,
        t.company_external_id,
        COUNT(DISTINCT t.meeting_external_id) AS transcripts_14d_meeting_count,
        COUNT(DISTINCT t.meeting_external_id) FILTER (
            WHERE EXISTS (
                SELECT 1
                FROM public.meeting_synopsis s
                WHERE s.meeting_external_id = t.meeting_external_id
                  AND s.user_external_id    = t.user_external_id
            )
        ) AS synopsised_14d_meeting_count
    FROM public.meeting_transcription t
    JOIN public.meeting m ON m.external_id = t.meeting_external_id
    JOIN cohort co ON co.user_external_id = t.user_external_id
                 AND co.company_external_id = t.company_external_id
    JOIN public.users u ON u.external_id = t.user_external_id
    WHERE m.is_demo = false
      AND m.deleted_date_utc IS NULL
      AND t.deleted_date_utc IS NULL
      AND t.created_date_utc >= u.created_at_utc
      AND t.created_date_utc <  u.created_at_utc + interval '14 days'
    GROUP BY t.user_external_id, t.company_external_id
),

synopses AS (
    SELECT
        s.user_external_id,
        s.company_external_id,
        COUNT(*)                          AS synopsis_count,
        MIN(s.created_date_utc)           AS first_synopsis_date,
        MAX(s.created_date_utc)           AS last_synopsis_date,
        (array_agg(s.created_date_utc ORDER BY s.created_date_utc ASC))[2]
                                          AS second_synopsis_date,
        (array_agg(s.created_date_utc ORDER BY s.created_date_utc ASC))[5]
                                          AS fifth_synopsis_date
    FROM public.meeting_synopsis s
    JOIN public.meeting m ON m.external_id = s.meeting_external_id
    JOIN cohort co ON co.user_external_id = s.user_external_id
                 AND co.company_external_id = s.company_external_id
    JOIN public.users u ON u.external_id = s.user_external_id
    WHERE m.is_demo = false
      AND m.deleted_date_utc IS NULL
      AND s.created_date_utc >= u.created_at_utc
    GROUP BY s.user_external_id, s.company_external_id
),

custom_templates AS (
    SELECT
        t.user_external_id,
        t.company_external_id,
        COUNT(*)                AS custom_template_count,
        MIN(t.created_date_utc) AS first_custom_template_date,
        (array_agg(t.created_date_utc ORDER BY t.created_date_utc ASC))[2]
                                AS second_custom_template_date,
        (array_agg(t.created_date_utc ORDER BY t.created_date_utc ASC))[5]
                                AS fifth_custom_template_date
    FROM public.template t
    JOIN public.users tu ON tu.external_id = t.user_external_id
    JOIN cohort co ON co.user_external_id = t.user_external_id
                 AND co.company_external_id = t.company_external_id
    WHERE t.created_date_utc > tu.created_at_utc + interval '5 minutes'
      AND t.is_archived = false
    GROUP BY t.user_external_id, t.company_external_id
),

calendar_first AS (
    SELECT
        cal.user_external_id,
        cal.company_external_id,
        MIN(cal.created_date_utc) AS first_calendar_date
    FROM public.calendar cal
    JOIN cohort co ON co.user_external_id = cal.user_external_id
                 AND co.company_external_id = cal.company_external_id
    JOIN public.users u ON u.external_id = cal.user_external_id
    WHERE cal.created_date_utc >= u.created_at_utc
    GROUP BY cal.user_external_id, cal.company_external_id
),

chats AS (
    SELECT
        c.user_external_id,
        c.company_external_id,
        COUNT(*) FILTER (
            WHERE c.meeting_external_id IS NOT NULL
               OR c.synopsis_external_id IS NOT NULL
        )                                  AS chat_meeting_count,
        COUNT(*) FILTER (
            WHERE c.client_external_id IS NOT NULL
        )                                  AS chat_client_count,
        MIN(c.created_date_utc) FILTER (
            WHERE c.meeting_external_id IS NOT NULL
               OR c.synopsis_external_id IS NOT NULL
        )                                  AS first_chat_meeting_date,
        (array_agg(c.created_date_utc ORDER BY c.created_date_utc ASC)
            FILTER (WHERE c.meeting_external_id IS NOT NULL
                       OR c.synopsis_external_id IS NOT NULL))[2]
                                           AS second_chat_meeting_date,
        (array_agg(c.created_date_utc ORDER BY c.created_date_utc ASC)
            FILTER (WHERE c.meeting_external_id IS NOT NULL
                       OR c.synopsis_external_id IS NOT NULL))[5]
                                           AS fifth_chat_meeting_date,
        MIN(c.created_date_utc) FILTER (
            WHERE c.client_external_id IS NOT NULL
        )                                  AS first_chat_client_date,
        (array_agg(c.created_date_utc ORDER BY c.created_date_utc ASC)
            FILTER (WHERE c.client_external_id IS NOT NULL))[2]
                                           AS second_chat_client_date,
        (array_agg(c.created_date_utc ORDER BY c.created_date_utc ASC)
            FILTER (WHERE c.client_external_id IS NOT NULL))[5]
                                           AS fifth_chat_client_date
    FROM public.chat c
    JOIN cohort co ON co.user_external_id = c.user_external_id
                 AND co.company_external_id = c.company_external_id
    JOIN public.users u ON u.external_id = c.user_external_id
    WHERE c.deleted_date_utc IS NULL
      AND c.created_date_utc >= u.created_at_utc
    GROUP BY c.user_external_id, c.company_external_id
),

documents AS (
    SELECT
        d.user_external_id,
        d.company_external_id,
        COUNT(*)                AS document_count,
        MIN(d.created_date_utc) AS first_document_date,
        (array_agg(d.created_date_utc ORDER BY d.created_date_utc ASC))[2]
                                AS second_document_date,
        (array_agg(d.created_date_utc ORDER BY d.created_date_utc ASC))[5]
                                AS fifth_document_date
    FROM public.document d
    JOIN cohort co ON co.user_external_id = d.user_external_id
                 AND co.company_external_id = d.company_external_id
    JOIN public.users u ON u.external_id = d.user_external_id
    WHERE d.created_date_utc >= u.created_at_utc
    GROUP BY d.user_external_id, d.company_external_id
),

tasks AS (
    SELECT
        t.created_by_user_external_id AS user_external_id,
        t.company_external_id,
        COUNT(*)                AS task_count,
        MIN(t.created_date_utc) AS first_task_date,
        (array_agg(t.created_date_utc ORDER BY t.created_date_utc ASC))[2]
                                AS second_task_date,
        (array_agg(t.created_date_utc ORDER BY t.created_date_utc ASC))[5]
                                AS fifth_task_date
    FROM public.task t
    JOIN cohort co ON co.user_external_id = t.created_by_user_external_id
                 AND co.company_external_id = t.company_external_id
    JOIN public.users u ON u.external_id = t.created_by_user_external_id
    WHERE t.deleted_date_utc IS NULL
      AND t.created_date_utc >= u.created_at_utc
    GROUP BY t.created_by_user_external_id, t.company_external_id
),

task_updates AS (
    SELECT
        ta.created_by_user_external_id AS user_external_id,
        ta.company_external_id,
        COUNT(*)                AS task_update_count,
        MIN(ta.created_date_utc) AS first_task_update_date
    FROM public.task_activity ta
    JOIN cohort co ON co.user_external_id = ta.created_by_user_external_id
                 AND co.company_external_id = ta.company_external_id
    JOIN public.users u ON u.external_id = ta.created_by_user_external_id
    WHERE ta.activity_type = 'task-updated'
      AND ta.created_date_utc >= u.created_at_utc
    GROUP BY ta.created_by_user_external_id, ta.company_external_id
),

clients_created AS (
    SELECT
        cl.created_by_user_external_id AS user_external_id,
        cl.company_external_id,
        COUNT(*)                AS client_count,
        MIN(cl.created_date_utc) AS first_client_date,
        (array_agg(cl.created_date_utc ORDER BY cl.created_date_utc ASC))[2]
                                AS second_client_date,
        (array_agg(cl.created_date_utc ORDER BY cl.created_date_utc ASC))[5]
                                AS fifth_client_date
    FROM public.clients cl
    JOIN cohort co ON co.user_external_id = cl.created_by_user_external_id
                 AND co.company_external_id = cl.company_external_id
    JOIN public.users u ON u.external_id = cl.created_by_user_external_id
    WHERE cl.deleted_date_utc IS NULL
      AND cl.created_date_utc >= u.created_at_utc
    GROUP BY cl.created_by_user_external_id, cl.company_external_id
),

forms AS (
    SELECT
        ff.created_by_user_external_id AS user_external_id,
        ff.company_external_id,
        COUNT(*)                AS form_count,
        MIN(ff.created_date_utc) AS first_form_date,
        (array_agg(ff.created_date_utc ORDER BY ff.created_date_utc ASC))[2]
                                AS second_form_date,
        (array_agg(ff.created_date_utc ORDER BY ff.created_date_utc ASC))[5]
                                AS fifth_form_date
    FROM public.filled_form ff
    JOIN cohort co ON co.user_external_id = ff.created_by_user_external_id
                 AND co.company_external_id = ff.company_external_id
    JOIN public.users u ON u.external_id = ff.created_by_user_external_id
    WHERE ff.created_date_utc >= u.created_at_utc
    GROUP BY ff.created_by_user_external_id, ff.company_external_id
),

company_size AS (
    SELECT
        ur.company_external_id,
        COUNT(DISTINCT ur.user_external_id) AS marloo_active_user_count
    FROM public.user_roles ur
    WHERE ur.status = 'active'
    GROUP BY ur.company_external_id
),

-- The earliest active user in each company; their signup is treated as the
-- 'company-creator' signup. Anyone joining later is treated as a team invite.
company_founders AS (
    SELECT DISTINCT ON (ur.company_external_id)
        ur.company_external_id,
        ur.user_external_id AS founder_user_external_id,
        u.created_at_utc    AS founder_created_at_utc
    FROM public.user_roles ur
    JOIN public.users u ON u.external_id = ur.user_external_id
    WHERE ur.status = 'active'
    ORDER BY ur.company_external_id, u.created_at_utc ASC
),

-- Attribute teammate-joins to the company founder only (non-founders did not
-- meaningfully "invite" anyone in this model). 1st / 2nd / 5th teammate join
-- dates let the conversion table threshold by 1+/2+/5+. No timestamp on
-- public.company_invite or public.user_roles, so we use users.created_at_utc
-- of the later-joining teammates as the proxy "invite landed" date.
team_invites AS (
    SELECT
        cf.founder_user_external_id AS user_external_id,
        cf.company_external_id,
        MIN(later.created_at_utc) FILTER (WHERE later.rn = 1) AS first_teammate_join_date,
        MIN(later.created_at_utc) FILTER (WHERE later.rn = 2) AS second_teammate_join_date,
        MIN(later.created_at_utc) FILTER (WHERE later.rn = 5) AS fifth_teammate_join_date,
        COUNT(*) AS teammate_join_count
    FROM company_founders cf
    JOIN LATERAL (
        SELECT u2.created_at_utc,
               row_number() OVER (ORDER BY u2.created_at_utc ASC) AS rn
        FROM public.user_roles ur2
        JOIN public.users u2 ON u2.external_id = ur2.user_external_id
        WHERE ur2.status = 'active'
          AND ur2.company_external_id = cf.company_external_id
          AND u2.external_id <> cf.founder_user_external_id
          AND u2.created_at_utc > cf.founder_created_at_utc
    ) later ON TRUE
    GROUP BY cf.founder_user_external_id, cf.company_external_id
),

billing AS (
    SELECT
        cb.company_external_id,
        bool_or(cb.billing_plan::text IN ('plus','pro')) AS on_paid_plan,
        MIN(cb.subscription_started_date_utc) FILTER (
            WHERE cb.billing_plan::text IN ('plus','pro')
        ) AS subscription_started_date_utc,
        MIN(cb.subscription_started_date_utc) FILTER (
            WHERE cb.billing_plan::text = 'plus'
        ) AS first_plus_date,
        MIN(cb.subscription_started_date_utc) FILTER (
            WHERE cb.billing_plan::text = 'pro'
        ) AS first_pro_date,
        (array_agg(cb.billing_plan::text ORDER BY cb.subscription_started_date_utc DESC NULLS LAST))[1] AS billing_plan,
        array_remove(array_agg(DISTINCT cb.stripe_customer_id), NULL) AS stripe_customer_ids
    FROM public.company_billing cb
    GROUP BY cb.company_external_id
)

SELECT
    u.external_id                AS user_external_id,
    co.company_external_id,
    u.email,
    TRIM(COALESCE(u.first_name,'') || ' ' || COALESCE(u.last_name,'')) AS full_name,
    u.created_at_utc             AS signup_date_utc,
    cmp.name                     AS company_name,
    cmp.country_code,

    cs.marloo_active_user_count,
    COALESCE(b.on_paid_plan, false) AS on_paid_plan,
    COALESCE(b.billing_plan, 'free') AS billing_plan,
    b.subscription_started_date_utc,
    b.first_plus_date,
    b.first_pro_date,
    b.stripe_customer_ids,

    (cf2.founder_user_external_id IS NOT NULL
        AND cf2.founder_user_external_id <> u.external_id) AS is_team_invite,

    cf.first_calendar_date,
    t.first_transcript_date,
    t.second_transcript_date,
    t.fifth_transcript_date,
    COALESCE(t.transcript_count, 0) AS transcript_count,
    s.first_synopsis_date,
    s.second_synopsis_date,
    s.fifth_synopsis_date,
    s.last_synopsis_date,
    COALESCE(s.synopsis_count, 0) AS synopsis_count,
    COALESCE(t14.transcripts_14d_meeting_count, 0) AS transcripts_14d_meeting_count,
    COALESCE(t14.synopsised_14d_meeting_count, 0) AS synopsised_14d_meeting_count,
    ct.first_custom_template_date,
    ct.second_custom_template_date,
    ct.fifth_custom_template_date,
    COALESCE(ct.custom_template_count, 0) AS custom_template_count,
    ch.first_chat_meeting_date,
    ch.second_chat_meeting_date,
    ch.fifth_chat_meeting_date,
    ch.first_chat_client_date,
    ch.second_chat_client_date,
    ch.fifth_chat_client_date,
    COALESCE(ch.chat_meeting_count, 0) AS chat_meeting_count,
    COALESCE(ch.chat_client_count, 0) AS chat_client_count,
    doc.first_document_date,
    doc.second_document_date,
    doc.fifth_document_date,
    COALESCE(doc.document_count, 0) AS document_count,
    tk.first_task_date,
    tk.second_task_date,
    tk.fifth_task_date,
    COALESCE(tk.task_count, 0) AS task_count,
    tu.first_task_update_date,
    COALESCE(tu.task_update_count, 0) AS task_update_count,
    cc.first_client_date,
    cc.second_client_date,
    cc.fifth_client_date,
    COALESCE(cc.client_count, 0) AS client_count,
    ff.first_form_date,
    ff.second_form_date,
    ff.fifth_form_date,
    COALESCE(ff.form_count, 0) AS form_count,
    ti.first_teammate_join_date,
    ti.second_teammate_join_date,
    ti.fifth_teammate_join_date,
    COALESCE(ti.teammate_join_count, 0) AS teammate_join_count,

    EXTRACT(EPOCH FROM (s.first_synopsis_date - u.created_at_utc)) / 86400.0 AS days_to_first_synopsis,
    EXTRACT(EPOCH FROM (now() - u.created_at_utc)) / 86400.0 AS days_since_signup

FROM cohort co
JOIN public.users u   ON u.external_id = co.user_external_id
LEFT JOIN public.companies cmp ON cmp.external_id = co.company_external_id
LEFT JOIN company_size      cs ON cs.company_external_id = co.company_external_id
LEFT JOIN billing            b ON b.company_external_id = co.company_external_id
LEFT JOIN transcripts        t ON t.user_external_id  = co.user_external_id
                              AND t.company_external_id = co.company_external_id
LEFT JOIN synopses           s ON s.user_external_id  = co.user_external_id
                              AND s.company_external_id = co.company_external_id
LEFT JOIN transcripts_14d  t14 ON t14.user_external_id  = co.user_external_id
                              AND t14.company_external_id = co.company_external_id
LEFT JOIN custom_templates  ct ON ct.user_external_id = co.user_external_id
                              AND ct.company_external_id = co.company_external_id
LEFT JOIN calendar_first    cf ON cf.user_external_id = co.user_external_id
                              AND cf.company_external_id = co.company_external_id
LEFT JOIN chats             ch ON ch.user_external_id = co.user_external_id
                              AND ch.company_external_id = co.company_external_id
LEFT JOIN documents        doc ON doc.user_external_id = co.user_external_id
                              AND doc.company_external_id = co.company_external_id
LEFT JOIN tasks             tk ON tk.user_external_id  = co.user_external_id
                              AND tk.company_external_id = co.company_external_id
LEFT JOIN task_updates      tu ON tu.user_external_id  = co.user_external_id
                              AND tu.company_external_id = co.company_external_id
LEFT JOIN clients_created   cc ON cc.user_external_id  = co.user_external_id
                              AND cc.company_external_id = co.company_external_id
LEFT JOIN forms             ff ON ff.user_external_id  = co.user_external_id
                              AND ff.company_external_id = co.company_external_id
LEFT JOIN company_founders  cf2 ON cf2.company_external_id = co.company_external_id
LEFT JOIN team_invites      ti  ON ti.user_external_id   = co.user_external_id
                              AND ti.company_external_id = co.company_external_id

ORDER BY u.created_at_utc DESC, co.company_external_id
`;
}

export const BASELINE_SQL = buildBaselineSql("baseline");
export const ALL_USERS_SQL = buildBaselineSql("all_users");
export const DEMO_SQL = buildBaselineSql("demo");
export const SUPPORT_NO_DEMO_SQL = buildBaselineSql("support_no_demo");
export const SUPPORT_DEMO_SQL = buildBaselineSql("support_demo");

// One-row-per-company aggregation over the same cohort. Powers the account/
// team-level insights section. Uses meeting-owner attribution for activation
// (meeting.created_by_user_external_id) so summaries produced on behalf of an
// adviser by support staff still credit the adviser whose meeting it is.
function buildCompanyInsightsSql(mode: CohortMode): string {
  return `
WITH ${cohortCtesSqlFor(mode)},

meeting_owner_synopses AS (
    SELECT
        m.created_by_user_external_id AS user_external_id,
        m.company_external_id,
        MIN(s.created_date_utc) FILTER (
            WHERE s.created_date_utc <= u.created_at_utc + interval '14 days'
        ) AS first_synopsis_in_14d,
        (array_agg(s.created_date_utc ORDER BY s.created_date_utc ASC)
            FILTER (WHERE s.created_date_utc <= u.created_at_utc + interval '14 days'))[2]
            AS second_synopsis_in_14d
    FROM public.meeting_synopsis s
    JOIN public.meeting m ON m.external_id = s.meeting_external_id
    JOIN public.users u   ON u.external_id = m.created_by_user_external_id
    JOIN cohort co ON co.user_external_id = m.created_by_user_external_id
                  AND co.company_external_id = m.company_external_id
    WHERE m.is_demo = false
      AND m.deleted_date_utc IS NULL
      AND s.created_date_utc >= u.created_at_utc
    GROUP BY m.created_by_user_external_id, m.company_external_id
),

founders AS (
    SELECT co.company_external_id,
           (array_agg(co.user_external_id ORDER BY u.created_at_utc ASC))[1] AS founder_user_external_id,
           MIN(u.created_at_utc) AS founder_signup_date_utc
    FROM cohort co
    JOIN public.users u ON u.external_id = co.user_external_id
    GROUP BY co.company_external_id
),

seats AS (
    SELECT ur.company_external_id,
           COUNT(DISTINCT ur.user_external_id) FILTER (WHERE ur.status = 'active') AS current_active_seats
    FROM public.user_roles ur
    GROUP BY ur.company_external_id
),

team_signups AS (
    SELECT f.company_external_id,
           COUNT(DISTINCT u.external_id) FILTER (
               WHERE u.external_id <> f.founder_user_external_id
                 AND u.created_at_utc > f.founder_signup_date_utc
           ) AS teammates_signed_up_after_founder,
           MIN(u.created_at_utc) FILTER (
               WHERE u.external_id <> f.founder_user_external_id
                 AND u.created_at_utc > f.founder_signup_date_utc
           ) AS first_teammate_signup_after_founder_utc
    FROM founders f
    JOIN public.user_roles ur ON ur.company_external_id = f.company_external_id AND ur.status = 'active'
    JOIN public.users u       ON u.external_id = ur.user_external_id
    GROUP BY f.company_external_id
)

SELECT
    f.company_external_id,
    cmp.name        AS company_name,
    cmp.country_code,
    f.founder_user_external_id,
    f.founder_signup_date_utc,
    COALESCE(s.current_active_seats, 0)                       AS current_active_seats,
    COALESCE(ts.teammates_signed_up_after_founder, 0)         AS teammates_signed_up_after_founder,
    ts.first_teammate_signup_after_founder_utc,
    (SELECT COUNT(*) FROM meeting_owner_synopses mos
       WHERE mos.company_external_id = f.company_external_id
         AND mos.user_external_id <> f.founder_user_external_id
         AND mos.second_synopsis_in_14d IS NOT NULL
    )                                                          AS non_founder_activated_count,
    EXISTS (SELECT 1 FROM meeting_owner_synopses mos
       WHERE mos.company_external_id = f.company_external_id
         AND mos.user_external_id = f.founder_user_external_id
         AND mos.second_synopsis_in_14d IS NOT NULL
    )                                                          AS founder_activated
FROM founders f
LEFT JOIN public.companies cmp ON cmp.external_id = f.company_external_id
LEFT JOIN seats             s  ON s.company_external_id  = f.company_external_id
LEFT JOIN team_signups     ts  ON ts.company_external_id = f.company_external_id
ORDER BY f.founder_signup_date_utc DESC
`;
}

export const COMPANY_INSIGHTS_BASELINE_SQL = buildCompanyInsightsSql("baseline");
export const COMPANY_INSIGHTS_ALL_USERS_SQL = buildCompanyInsightsSql("all_users");
export const COMPANY_INSIGHTS_DEMO_SQL = buildCompanyInsightsSql("demo");
export const COMPANY_INSIGHTS_SUPPORT_NO_DEMO_SQL = buildCompanyInsightsSql("support_no_demo");
export const COMPANY_INSIGHTS_SUPPORT_DEMO_SQL = buildCompanyInsightsSql("support_demo");

export type CompanyInsightsRow = {
  company_external_id: string;
  company_name: string | null;
  country_code: string | null;
  founder_user_external_id: string;
  founder_signup_date_utc: string;
  current_active_seats: number;
  teammates_signed_up_after_founder: number;
  first_teammate_signup_after_founder_utc: string | null;
  non_founder_activated_count: number;
  founder_activated: boolean;
};

export function mapCompanyInsights(columns: string[], rows: unknown[][]): CompanyInsightsRow[] {
  const idx: Record<string, number> = {};
  columns.forEach((c, i) => { idx[c] = i; });
  const get = (row: unknown[], k: string): unknown =>
    idx[k] !== undefined ? row[idx[k] as number] : undefined;
  return rows.map((r) => ({
    company_external_id: str(get(r, "company_external_id")),
    company_name: strOrNull(get(r, "company_name")),
    country_code: strOrNull(get(r, "country_code")),
    founder_user_external_id: str(get(r, "founder_user_external_id")),
    founder_signup_date_utc: str(get(r, "founder_signup_date_utc")),
    current_active_seats: num(get(r, "current_active_seats")),
    teammates_signed_up_after_founder: num(get(r, "teammates_signed_up_after_founder")),
    first_teammate_signup_after_founder_utc: strOrNull(get(r, "first_teammate_signup_after_founder_utc")),
    non_founder_activated_count: num(get(r, "non_founder_activated_count")),
    founder_activated: bool(get(r, "founder_activated")),
  }));
}

// Runs against the Stripe sync DB (useAdminQuery option { database: "stripe" }).
// Returns the earliest paid invoice timestamp per Stripe customer, so the page
// can recover paid converters who exist as Stripe customers and were invoiced
// outside of any subscription (manual-invoice flow). Customers billed entirely
// off-platform will not appear here.
export const STRIPE_FIRST_PAID_INVOICE_SQL = `
SELECT
    i.customer AS stripe_customer_id,
    to_char(
        to_timestamp(MIN((i.status_transitions->>'paid_at')::bigint)),
        'YYYY-MM-DD"T"HH24:MI:SS"Z"'
    ) AS first_paid_at
FROM stripe.invoices i
WHERE i.status = 'paid'
  AND i.amount_paid > 0
  AND (i.status_transitions->>'paid_at') IS NOT NULL
  AND i.customer IS NOT NULL
GROUP BY i.customer
`;

export function mapStripeFirstPaid(columns: string[], rows: unknown[][]): Map<string, string> {
  const idx: Record<string, number> = {};
  columns.forEach((c, i) => { idx[c] = i; });
  const out = new Map<string, string>();
  for (const r of rows) {
    const id = strOrNull(r[idx.stripe_customer_id as number]);
    const at = strOrNull(r[idx.first_paid_at as number]);
    if (id && at) out.set(id, at);
  }
  return out;
}

export function enrichPaidConvertedDate(rows: BaselineRow[], firstPaidByCustomer: Map<string, string>): BaselineRow[] {
  if (firstPaidByCustomer.size === 0) return rows;
  return rows.map((u) => {
    if (u.subscription_started_date_utc != null) return u;
    if (u.stripe_customer_ids.length === 0) return u;
    let earliest: string | null = null;
    for (const cid of u.stripe_customer_ids) {
      const at = firstPaidByCustomer.get(cid);
      if (!at) continue;
      if (earliest == null || at < earliest) earliest = at;
    }
    if (earliest == null) return u;
    if (earliest <= u.signup_date_utc) return u;
    return { ...u, paid_converted_date_utc: earliest };
  });
}

export type BaselineRow = {
  user_external_id: string;
  company_external_id: string | null;
  email: string;
  full_name: string;
  signup_date_utc: string;
  company_name: string | null;
  country_code: string | null;
  marloo_active_user_count: number;
  on_paid_plan: boolean;
  billing_plan: string;
  subscription_started_date_utc: string | null;
  first_plus_date: string | null;
  first_pro_date: string | null;
  stripe_customer_ids: string[];
  paid_converted_date_utc: string | null;
  is_team_invite: boolean;
  first_calendar_date: string | null;
  first_transcript_date: string | null;
  second_transcript_date: string | null;
  fifth_transcript_date: string | null;
  transcript_count: number;
  first_synopsis_date: string | null;
  second_synopsis_date: string | null;
  fifth_synopsis_date: string | null;
  last_synopsis_date: string | null;
  synopsis_count: number;
  transcripts_14d_meeting_count: number;
  synopsised_14d_meeting_count: number;
  first_custom_template_date: string | null;
  second_custom_template_date: string | null;
  fifth_custom_template_date: string | null;
  custom_template_count: number;
  first_chat_meeting_date: string | null;
  second_chat_meeting_date: string | null;
  fifth_chat_meeting_date: string | null;
  first_chat_client_date: string | null;
  second_chat_client_date: string | null;
  fifth_chat_client_date: string | null;
  chat_meeting_count: number;
  chat_client_count: number;
  first_document_date: string | null;
  second_document_date: string | null;
  fifth_document_date: string | null;
  document_count: number;
  first_task_date: string | null;
  second_task_date: string | null;
  fifth_task_date: string | null;
  task_count: number;
  first_task_update_date: string | null;
  task_update_count: number;
  first_client_date: string | null;
  second_client_date: string | null;
  fifth_client_date: string | null;
  client_count: number;
  first_form_date: string | null;
  second_form_date: string | null;
  fifth_form_date: string | null;
  form_count: number;
  first_teammate_join_date: string | null;
  second_teammate_join_date: string | null;
  fifth_teammate_join_date: string | null;
  teammate_join_count: number;
  days_to_first_synopsis: number | null;
  days_since_signup: number;
};

const num = (v: unknown): number => {
  if (v == null) return 0;
  if (typeof v === "number") return v;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const numOrNull = (v: unknown): number | null => {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const str = (v: unknown): string => (v == null ? "" : String(v));
const strOrNull = (v: unknown): string | null => (v == null ? null : String(v));
const bool = (v: unknown): boolean => v === true || v === "t" || v === "true";
const strArray = (v: unknown): string[] => {
  if (Array.isArray(v)) return v.filter((x) => x != null).map(String);
  if (typeof v === "string" && v.startsWith("{") && v.endsWith("}")) {
    const inner = v.slice(1, -1);
    if (inner === "") return [];
    return inner.split(",").map((s) => s.replace(/^"|"$/g, "")).filter(Boolean);
  }
  return [];
};

export function mapRows(columns: string[], rows: unknown[][]): BaselineRow[] {
  const idx: Record<string, number> = {};
  columns.forEach((c, i) => { idx[c] = i; });
  const get = (row: unknown[], k: string): unknown =>
    idx[k] !== undefined ? row[idx[k] as number] : undefined;
  return rows.map((r) => ({
    user_external_id: str(get(r, "user_external_id")),
    company_external_id: strOrNull(get(r, "company_external_id")),
    email: str(get(r, "email")),
    full_name: str(get(r, "full_name")),
    signup_date_utc: str(get(r, "signup_date_utc")),
    company_name: strOrNull(get(r, "company_name")),
    country_code: strOrNull(get(r, "country_code")),
    marloo_active_user_count: num(get(r, "marloo_active_user_count")),
    on_paid_plan: bool(get(r, "on_paid_plan")),
    billing_plan: str(get(r, "billing_plan")) || "free",
    subscription_started_date_utc: strOrNull(get(r, "subscription_started_date_utc")),
    first_plus_date: strOrNull(get(r, "first_plus_date")),
    first_pro_date: strOrNull(get(r, "first_pro_date")),
    stripe_customer_ids: strArray(get(r, "stripe_customer_ids")),
    paid_converted_date_utc: strOrNull(get(r, "subscription_started_date_utc")),
    is_team_invite: bool(get(r, "is_team_invite")),
    first_calendar_date: strOrNull(get(r, "first_calendar_date")),
    first_transcript_date: strOrNull(get(r, "first_transcript_date")),
    second_transcript_date: strOrNull(get(r, "second_transcript_date")),
    fifth_transcript_date: strOrNull(get(r, "fifth_transcript_date")),
    transcript_count: num(get(r, "transcript_count")),
    first_synopsis_date: strOrNull(get(r, "first_synopsis_date")),
    second_synopsis_date: strOrNull(get(r, "second_synopsis_date")),
    fifth_synopsis_date: strOrNull(get(r, "fifth_synopsis_date")),
    last_synopsis_date: strOrNull(get(r, "last_synopsis_date")),
    synopsis_count: num(get(r, "synopsis_count")),
    transcripts_14d_meeting_count: num(get(r, "transcripts_14d_meeting_count")),
    synopsised_14d_meeting_count: num(get(r, "synopsised_14d_meeting_count")),
    first_custom_template_date: strOrNull(get(r, "first_custom_template_date")),
    second_custom_template_date: strOrNull(get(r, "second_custom_template_date")),
    fifth_custom_template_date: strOrNull(get(r, "fifth_custom_template_date")),
    custom_template_count: num(get(r, "custom_template_count")),
    first_chat_meeting_date: strOrNull(get(r, "first_chat_meeting_date")),
    second_chat_meeting_date: strOrNull(get(r, "second_chat_meeting_date")),
    fifth_chat_meeting_date: strOrNull(get(r, "fifth_chat_meeting_date")),
    first_chat_client_date: strOrNull(get(r, "first_chat_client_date")),
    second_chat_client_date: strOrNull(get(r, "second_chat_client_date")),
    fifth_chat_client_date: strOrNull(get(r, "fifth_chat_client_date")),
    chat_meeting_count: num(get(r, "chat_meeting_count")),
    chat_client_count: num(get(r, "chat_client_count")),
    first_document_date: strOrNull(get(r, "first_document_date")),
    second_document_date: strOrNull(get(r, "second_document_date")),
    fifth_document_date: strOrNull(get(r, "fifth_document_date")),
    document_count: num(get(r, "document_count")),
    first_task_date: strOrNull(get(r, "first_task_date")),
    second_task_date: strOrNull(get(r, "second_task_date")),
    fifth_task_date: strOrNull(get(r, "fifth_task_date")),
    task_count: num(get(r, "task_count")),
    first_task_update_date: strOrNull(get(r, "first_task_update_date")),
    task_update_count: num(get(r, "task_update_count")),
    first_client_date: strOrNull(get(r, "first_client_date")),
    second_client_date: strOrNull(get(r, "second_client_date")),
    fifth_client_date: strOrNull(get(r, "fifth_client_date")),
    client_count: num(get(r, "client_count")),
    first_form_date: strOrNull(get(r, "first_form_date")),
    second_form_date: strOrNull(get(r, "second_form_date")),
    fifth_form_date: strOrNull(get(r, "fifth_form_date")),
    form_count: num(get(r, "form_count")),
    first_teammate_join_date: strOrNull(get(r, "first_teammate_join_date")),
    second_teammate_join_date: strOrNull(get(r, "second_teammate_join_date")),
    fifth_teammate_join_date: strOrNull(get(r, "fifth_teammate_join_date")),
    teammate_join_count: num(get(r, "teammate_join_count")),
    days_to_first_synopsis: numOrNull(get(r, "days_to_first_synopsis")),
    days_since_signup: num(get(r, "days_since_signup")),
  }));
}
